import { useAuth } from 'hooks';
import { useState } from 'react';
import { SupplierInfo, SupportedBackOffices } from 'types';
import { CarGasBackofficeForm } from './CarGasBackofficeForm/CarGasBackofficeForm';
import { AddsBackofficeForm } from './AddsBackofficeForm/AddsBackofficeForm';
import { NoneBackofficeForm } from './NoneBackofficeForm/NoneBackofficeForm';
import { FtpBackofficeForm } from './FtpBackofficeForm/FtpBackofficeForm';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import {
    BackofficeOption,
    backofficeOptions,
} from 'lib/thirdPartyBackOfficeConstants';
import styles from '../../Organization.module.scss';

export interface OrganizationBackofficeProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
    supplierInfo: SupplierInfo;
}

/** Admin-only supplier configuration page to set third-party backoffice settings. */
export const OrganizationBackoffice = ({
    viewAsSupplier,
    supplierInfo,
}: OrganizationBackofficeProps) => {
    const { user } = useAuth();
    const [selectedBackoffice, setSelectedBackoffice] =
        useState<SupportedBackOffices>(supplierInfo?.backoffice ?? '');

    const handleDropdownChange = (event: SelectChangeEvent) => {
        setSelectedBackoffice(event.target.value as SupportedBackOffices);
    };

    return (
        <div data-testid="OrganizationBackoffice" className={styles.container}>
            <Box sx={{ minWidth: 120, marginTop: '10px' }}>
                <FormControl fullWidth>
                    <InputLabel>Back Office</InputLabel>
                    <Select
                        id="backoffice-selection"
                        value={selectedBackoffice}
                        label="Back Office"
                        onChange={handleDropdownChange}
                    >
                        {backofficeOptions.map((option: BackofficeOption) => {
                            return (
                                <MenuItem
                                    value={option.value}
                                    key={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Box>

            {user && (supplierInfo.backoffice || selectedBackoffice) ? (
                <>
                    {selectedBackoffice === 'cargas' ? (
                        <CarGasBackofficeForm
                            supplierId={
                                viewAsSupplier ??
                                user?.protected?.supplierId ??
                                ''
                            }
                            supplierInfo={supplierInfo}
                            user={user}
                        />
                    ) : null}
                    {selectedBackoffice === 'adds' ? (
                        <AddsBackofficeForm
                            supplierId={
                                viewAsSupplier ??
                                user?.protected?.supplierId ??
                                ''
                            }
                            supplierInfo={supplierInfo}
                            user={user}
                        />
                    ) : null}
                    {selectedBackoffice === 'none' ? (
                        <NoneBackofficeForm />
                    ) : null}
                    {!['cargas', 'adds', 'none'].includes(
                        selectedBackoffice
                    ) ? (
                        <FtpBackofficeForm
                            supplierId={
                                viewAsSupplier ??
                                user?.protected?.supplierId ??
                                ''
                            }
                            supplierInfo={supplierInfo}
                            backofficeName={selectedBackoffice ?? ''}
                            user={user}
                        />
                    ) : null}
                </>
            ) : (
                <div>Select a Back Office</div>
            )}
        </div>
    );
};
